import { useCallback, useEffect, useState } from 'react';
import type { PrinterView } from 'shared-models/browser';
import {
  FIXED_PRINTER_STORAGE_KEY,
  clearFixedPrinterId,
  getFixedPrinterId,
  resolveFixedPrinter,
  setFixedPrinterId,
} from './fixed-printer.util';

type UseFixedPrinterResult = {
  fixedPrinterId: number | null;
  setFixedPrinter: (printerId: number) => void;
  clearFixedPrinter: () => void;
  resolveAgainst: (activePrinters: PrinterView[]) => number | null;
};

// Device-local fixed-printer state on its own key, with cross-tab storage sync.
export function useFixedPrinter(): UseFixedPrinterResult {
  const [fixedPrinterId, setFixedPrinterIdState] = useState<number | null>(() => getFixedPrinterId());

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === FIXED_PRINTER_STORAGE_KEY) {
        setFixedPrinterIdState(getFixedPrinterId());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setFixedPrinter = useCallback((printerId: number) => {
    setFixedPrinterId(printerId);
    setFixedPrinterIdState(printerId);
  }, []);

  const clearFixedPrinter = useCallback(() => {
    clearFixedPrinterId();
    setFixedPrinterIdState(null);
  }, []);

  const resolveAgainst = useCallback((activePrinters: PrinterView[]) => resolveFixedPrinter(activePrinters), []);

  return { fixedPrinterId, setFixedPrinter, clearFixedPrinter, resolveAgainst };
}
